import { useCallback, useEffect, useRef, useState } from "react";
import { useCreate, useGetList } from "react-admin";

import {
  InventorizationIncorrectStatus,
  InventorizationStatus,
} from "../../../entities/InventorizationStatus";
import { InventorizationStatusData, configApi } from "../services/configApi";

export interface CreateStatusParams {
  placeId: number;
  status: InventorizationStatus;
  incorrectStatus?: InventorizationIncorrectStatus;
  note?: string;
  sscc?: string | null;
  /** Whether the system expects no pallet at the place — decides if the place is reset. */
  isPlaceEmpty: boolean;
}

/** The pallet an initial-mode scan was matched to, as far as the operator got. */
export interface PalletMatch {
  /** Absent when the operator typed a pallet number the system knows no pallet for. */
  palletId?: number | null;
  palletExtId?: string | null;
  /** The incoming order: carried by the matched pallet, or picked by the operator without one. */
  orderId?: number | null;
  /** Boxes counted on the pallet: proposed from the matched pallet, corrected by the operator. */
  quantity?: number | null;
}

/** What a place shows on the canvas, condensed from the records of every pallet scanned there. */
export interface PlaceStatus {
  status: InventorizationStatus | null;
  incorrectStatus?: InventorizationIncorrectStatus | null;
}

/**
 * A place holds one record per scanned pallet, so a single problem among them makes the
 * whole place incorrect.
 */
const mergePlaceStatus = (current: PlaceStatus | undefined, next: PlaceStatus): PlaceStatus => (
  current?.status === InventorizationStatus.incorrect ? current : next
);

const groupStatusesByPlace = (
  records: InventorizationStatusData[]
): Record<number, PlaceStatus> => {
  const byPlace: Record<number, PlaceStatus> = {};

  for (const record of records) {
    if (!record.place_id) continue;
    byPlace[record.place_id] = mergePlaceStatus(byPlace[record.place_id], {
      status: record.status,
      incorrectStatus: record.incorrect_status,
    });
  }

  return byPlace;
};

/** Mirrors the backend rule that wipes the records of a place reported as empty. */
const clearsPlaceRecords = (params: CreateStatusParams): boolean => (
  params.status === InventorizationStatus.correct && params.isPlaceEmpty
);

export const useInventorizationStatuses = (terminalId: number | undefined) => {
  const {
    data: fetchedStatuses = {},
    loading,
    refetch,
  } = useGetList<InventorizationStatusData>(
    "storage-inventorization-statuses",
    { page: 1, perPage: 1 },
    { field: "place_address", order: "ASC" },
    { terminal_id: terminalId, no_range: true },
    { enabled: !!terminalId }
  );

  const [statuses, setStatuses] = useState<Record<number, PlaceStatus>>({});
  const initializedForTerminalRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!loading && terminalId !== initializedForTerminalRef.current) {
      setStatuses(groupStatusesByPlace(Object.values(fetchedStatuses)));
      initializedForTerminalRef.current = terminalId;
    }
  }, [fetchedStatuses, loading, terminalId]);

  const [create] = useCreate();

  const createStatus = useCallback(
    async (params: CreateStatusParams) => {
      const { placeId, status, incorrectStatus, note, sscc } = params;
      const scanned: PlaceStatus = { status, incorrectStatus };

      setStatuses((prev) => ({
        ...prev,
        [placeId]: clearsPlaceRecords(params)
          ? scanned
          : mergePlaceStatus(prev[placeId], scanned),
      }));

      await create("storage-inventorization-statuses", {
        terminal_id: terminalId,
        place_id: placeId,
        status,
        incorrect_status: incorrectStatus,
        note,
        sscc,
      });
    },
    [create, terminalId]
  );

  const setPlaceStatus = useCallback((placeId: number, status: PlaceStatus) => {
    setStatuses((prev) => ({ ...prev, [placeId]: status }));
  }, []);

  // The initial-mode writes paint the canvas only once the server accepted them: an initial
  // inventorization is the only record of what stands where, so a place must never look
  // counted when the write failed.

  const markPlaceEmpty = useCallback(
    async (placeId: number) => {
      if (!terminalId) return;
      await configApi.clearInventorizationStatuses(terminalId, placeId);
      await create("storage-inventorization-statuses", {
        terminal_id: terminalId,
        place_id: placeId,
        status: InventorizationStatus.correct,
        note: "",
      });
      setPlaceStatus(placeId, { status: InventorizationStatus.correct });
    },
    [create, terminalId, setPlaceStatus]
  );

  const recordScannedPallet = useCallback(
    async (placeId: number, sscc: string, match?: PalletMatch) => {
      if (!terminalId) return;
      await create("storage-inventorization-statuses", {
        terminal_id: terminalId,
        place_id: placeId,
        status: InventorizationStatus.incorrect,
        incorrect_status: InventorizationIncorrectStatus.place_empty,
        note: "",
        sscc,
        pallet_id: match?.palletId ?? null,
        pallet_ext_id: match?.palletExtId ?? null,
        order_id: match?.orderId ?? null,
        quantity: match?.quantity ?? null,
      });
      setPlaceStatus(placeId, {
        status: InventorizationStatus.incorrect,
        incorrectStatus: InventorizationIncorrectStatus.place_empty,
      });
    },
    [create, terminalId, setPlaceStatus]
  );

  const deleteStatus = useCallback(async (statusId: number) => {
    if (!terminalId) return;
    await configApi.deleteInventorizationStatus(terminalId, statusId);
  }, [terminalId]);

  /**
   * Repaint a place from the records it holds now. Deleting one of them can turn the place
   * correct or leave it with nothing at all, which no single write can be derived from.
   */
  const setPlaceStatusFromRecords = useCallback(
    (placeId: number, placeRecords: InventorizationStatusData[]) => {
      const placeStatus = groupStatusesByPlace(placeRecords)[placeId];

      setStatuses((prev) => {
        const next = { ...prev };
        if (placeStatus) {
          next[placeId] = placeStatus;
        } else {
          delete next[placeId];
        }
        return next;
      });
    },
    []
  );

  const clearPlace = useCallback(
    async (placeId: number) => {
      if (!terminalId) return;
      await configApi.clearInventorizationStatuses(terminalId, placeId);
      setStatuses((prev) => {
        const next = { ...prev };
        delete next[placeId];
        return next;
      });
    },
    [terminalId]
  );

  return {
    statuses,
    loading,
    createStatus,
    markPlaceEmpty,
    recordScannedPallet,
    deleteStatus,
    setPlaceStatusFromRecords,
    clearPlace,
    refetch,
  };
};
